import React, { useState, useEffect, useMemo } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

export default function UserManagement() {
  const currentUser = useMemo(() => JSON.parse(localStorage.getItem("user")), []);
  const isSpecialUser = currentUser?.email === "ratnanjali@islorg.com";
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [chatHistories, setChatHistories] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [view, setView] = useState("users");
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [actionUser, setActionUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [ingestions, setIngestions] = useState([]);
  const [ingestionError, setIngestionError] = useState("");
  const [userIngestionSelections, setUserIngestionSelections] = useState({});
  const [summaryModal, setSummaryModal] = useState({
    open: false,
    loading: false,
    text: "",
    user: null
  });

  const [searchInput, setSearchInput] = useState("");

  // Function to fetch user list
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (roleFilter) params.append("role", roleFilter);
      if (verifiedFilter) params.append("verified", verifiedFilter);
      const res = await fetchWithAuth(`/api/admin/users?${params.toString()}`, {
        method: "GET"
      });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data.users);
      if (isSpecialUser) {
        setUserIngestionSelections(prev => {
          const next = { ...prev };
          data.users.forEach(user => {
            if (user.role !== "student") {
              if (!next[user.uid]) {
                next[user.uid] = user.default_ingestion_id || "";
              }
            }
          });
          return next;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDownload = async (key) => {
    try {
      const res = await fetchWithAuth(`/api/admin/download-file?key=${encodeURIComponent(key)}`, { method: "GET" });
      if (!res.ok) throw new Error("Failed to get download URL");
      const { url } = await res.json();
      if (url) window.open(url, "_blank");
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleVerify = async (email) => {
    setActionLoading(true);
    try {
      const res = await fetchWithAuth("/api/admin/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error("Verification failed");
      alert(`${email} has been marked verified.`);
      // Refresh list
      setActionUser(null);
      // Trigger reload of users
      setView("users"); // ensure view is users
      await loadUsers();
      // reuse useEffect dependency to reload
    } catch (err) {
      console.error(err);
      alert("Error verifying user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnverifyUser = async (email) => {
    setActionLoading(true);
    try {
      const res = await fetchWithAuth("/api/admin/unverify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error("Unverify failed");
      alert(`${email} has been marked as unverified (inactive).`);
      setView("users");
      await loadUsers();
      setActionUser(null);
      // Refresh user list
    } catch (err) {
      console.error(err);
      alert("Error marking user inactive.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (email) => {
    setActionLoading(true);
    // Warn about data deletion
    const confirmMsg =
      "Deleting this user will permanently remove all their data.\n" +
      "If you only want to deactivate the user, please use 'Mark Unverified' instead.\n\n" +
      "Do you still want to delete?";
    if (!window.confirm(confirmMsg)) {
      setActionLoading(false);
      return;
    }
    try {
      const res = await fetchWithAuth("/api/admin/delete-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error("Deletion failed");
      alert(`${email} and all data have been deleted.`);
      await loadUsers();
      setActionUser(null);
      // optionally remove from UI immediately
      setUsers(users.filter(u => u.email !== email));
    } catch (err) {
      console.error(err);
      alert("Error deleting user.");
    } finally {
      setActionLoading(false);
    }
  };

  const loadIngestions = async () => {
    if (!isSpecialUser) return;
    try {
      // const response = await fetchWithAuth("/api/drive_rag/list_ingestions");
      const response = await fetchWithAuth("/api/drive_rag/new/list-ingestions");
      if (!response) throw new Error("No response");
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to fetch ingestions");
      }
      const data = await response.json();
      setIngestions(Array.isArray(data.ingestions) ? data.ingestions : []);
      setIngestionError("");
    } catch (err) {
      console.error(err);
      setIngestionError(err.message || "Failed to fetch ingestions");
      setIngestions([]);
    }
  };

  const handleAssignDefaultIngestion = async (user) => {
    const ingestionId = userIngestionSelections[user.uid] || user.default_ingestion_id || "";
    if (!ingestionId) {
      alert("Select an ingestion first.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetchWithAuth("/api/admin/assign-default-ingestion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, ingestion_id: ingestionId })
      });
      if (!res.ok) throw new Error("Assignment failed");
      alert(`Default ingestion assigned to ${user.email}.`);
      await loadUsers();
    } catch (err) {
      console.error(err);
      alert(err.message || "Error assigning ingestion.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSummariseChats = async (user) => {
    setSummaryModal({
      open: true,
      loading: true,
      text: "",
      user
    });
    try {
      const res = await fetchWithAuth("/api/admin/summarise-student-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to summarise chat");
      }
      const data = await res.json();
      setSummaryModal(prev => ({
        ...prev,
        loading: false,
        text: data.text || "No summary available."
      }));
    } catch (err) {
      console.error(err);
      setSummaryModal(prev => ({
        ...prev,
        loading: false,
        text: "Unable to fetch chat summary. Please try again."
      }));
    }
  };

  useEffect(() => {
    async function loadStudents() {
      setLoadingStudents(true);
      try {
        const res = await fetchWithAuth("/api/admin/students", { method: "GET" });
        if (!res.ok) throw new Error("Failed to fetch students");
        const data = await res.json();
        setStudents(data.students);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingStudents(false);
      }
    }
    loadStudents();
  }, []);

  useEffect(() => {
    if (isSpecialUser) {
      loadIngestions();
    }
  }, [isSpecialUser]);

  useEffect(() => {
    if (!selectedStudent) return;
    async function loadChats() {
      setLoadingChats(true);
      try {
        const res = await fetchWithAuth(`/api/admin/chat-history/${selectedStudent.id}`, { method: "GET" });
        if (!res.ok) throw new Error("Failed to fetch chat histories");
        const data = await res.json();
        setChatHistories(data.chats);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingChats(false);
      }
    }
    loadChats();
  }, [selectedStudent]);

  // Fetch users when switching to the User List view or when filters/search change
  useEffect(() => {
    if (view === "users") loadUsers();
  }, [view, searchTerm, roleFilter, verifiedFilter]);

  // Ensure the search input field shows the current searchTerm whenever we enter the User List view
  useEffect(() => {
    if (view === "users") {
      setSearchInput(searchTerm);
    }
  }, [view, searchTerm]);

  if (loadingStudents) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Loading students...</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative">
      {/* Top nav */}
      <nav className="flex border-b bg-white">
        <button
          onClick={() => setView("users")}
          className={`flex-1 p-3 ${
            view === "users" ? "bg-purple-100 font-semibold" : "hover:bg-gray-100"
          }`}
        >
          User List
        </button>
        <button
          onClick={() => setView("chats")}
          className={`flex-1 p-3 ${
            view === "chats" ? "bg-purple-100 font-semibold" : "hover:bg-gray-100"
          }`}
        >
          Chat Monitoring
        </button>
      </nav>
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Aside panel */}
        <aside className={`flex flex-col ${view === "users" ? "w-full" : "w-1/3"} bg-gray-50 overflow-y-auto p-4 min-h-0`}>
          {view === "chats" ? (
            <>
              <h3 className="text-lg font-semibold mb-2">Students</h3>
              <ul className="flex-1">
                {students.map(student => (
                  <li
                    key={student.id}
                    onClick={() => setSelectedStudent(student)}
                    className={`p-2 cursor-pointer ${
                      selectedStudent?.id === student.id
                        ? "bg-purple-100"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {student.name}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-2">All Users</h3>
              {/* Search & Filters */}
              <div className="mb-4 space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Search by email or name"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                  />
                  <button
                    onClick={() => setSearchTerm(searchInput)}
                    disabled={searchInput === searchTerm}
                    className={`px-3 py-1 rounded ${
                      searchInput !== searchTerm
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Search
                  </button>
                </div>
                <div className="flex space-x-2">
                  <select
                    value={roleFilter}
                    onChange={e => setRoleFilter(e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                    <option value="teacher">Teacher</option>
                    <option value="student">Student</option>
                  </select>
                  <select
                    value={verifiedFilter}
                    onChange={e => setVerifiedFilter(e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                  >
                    <option value="">Any Verification</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                  </select>
                </div>
              </div>
              {loadingUsers ? (
                <p>Loading users...</p>
              ) : (
                <ul className="flex-1 overflow-y-auto">
                  {users.length === 0 ? (
                    <li className="text-gray-500">No users to display</li>
                  ) : (
                    users.map(u => (
                      <li key={u.uid} className="p-2 border-b">
                        <div
                          onClick={() => setActionUser(u)}
                          className="cursor-pointer"
                        >
                          <div className="font-semibold">{u.name}</div>
                          <div className="text-sm">{u.email}</div>
                          <div className="text-xs text-gray-600">
                            Role: {u.role} | {u.verified ? "Verified" : "Unverified"}
                          </div>
                        </div>
                        {actionUser?.uid === u.uid && (
                          <div className="mt-2 space-y-2">
                            {isSpecialUser && u.role !== "student" && (
                              <div className="rounded-lg border border-slate-200 p-3">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Default Ingestion
                                </label>
                                <select
                                  value={userIngestionSelections[u.uid] ?? u.default_ingestion_id ?? ""}
                                  onChange={e =>
                                    setUserIngestionSelections(prev => ({
                                      ...prev,
                                      [u.uid]: e.target.value
                                    }))
                                  }
                                  className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-sm focus:border-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-200"
                                >
                                  <option value="">Select ingestion</option>
                                  {ingestions.map(ing => {
                                    const ingestionId = ing.ingestion_id || ing.document_id || "";
                                    return (
                                      <option key={`assign-${ingestionId}`} value={ingestionId}>
                                        {ing.folder_name || ingestionId}
                                      </option>
                                    );
                                  })}
                                </select>
                                {ingestionError && (
                                  <p className="mt-1 text-xs text-red-500">{ingestionError}</p>
                                )}
                                <button
                                  onClick={() => handleAssignDefaultIngestion(u)}
                                  className="mt-2 w-full rounded bg-purple-600 px-2 py-1 text-sm font-medium text-white hover:bg-purple-700"
                                >
                                  Assign Default Ingestion
                                </button>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {!u.verified && (
                                <button
                                  onClick={() => handleVerify(u.email)}
                                  className="px-2 py-1 bg-green-500 text-white rounded"
                                >
                                  Mark Verified
                                </button>
                              )}
                              {u.verified && (
                                <button
                                  onClick={() => handleUnverifyUser(u.email)}
                                  className="px-2 py-1 bg-orange-500 text-white rounded"
                                >
                                  Mark Unverified
                                </button>
                              )}
                              {u.role === "student" && (
                                <button
                                  onClick={() => handleSummariseChats(u)}
                                  className="px-2 py-1 bg-blue-500 text-white rounded"
                                >
                                  Summarise Chat
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(u.email)}
                                className="px-2 py-1 bg-red-500 text-white rounded"
                              >
                                Delete User
                              </button>
                              <button
                                onClick={() => setActionUser(null)}
                                className="px-2 py-1 bg-gray-300 text-black rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </>
          )}
        </aside>
 
        {/* Main content */}
        {view === "chats" && (
          <main className="flex-1 min-h-0 bg-white p-6 overflow-y-auto">
            <>
              {selectedStudent && loadingChats && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                  <p className="text-lg text-gray-500">Loading chats...</p>
                </div>
              )}
              {!selectedStudent && (
                <div className="flex items-center justify-center flex-1 text-gray-400">
                  <p className="text-xl">Select a student to view their chat history.</p>
                </div>
              )}
              {selectedStudent && chatHistories && (
                <>
                  <h3 className="text-xl font-semibold mb-4">
                    {selectedStudent.name}&apos;s Chats
                  </h3>
                  {["default", "writing"].map(mode => (
                    <div key={mode} className="mb-6 border-b pb-4">
                      <h4 className="text-lg font-medium mb-2">
                        {mode === "default"
                          ? "Default Chat"
                          : "Writing Assistant Chat"}
                      </h4>
                      <div className="space-y-4">
                        {(chatHistories[mode] || []).map((msg, idx) => (
                          <div key={idx} className="p-3 rounded bg-gray-100 flex flex-col">
                            <div>
                              <span className="font-semibold">
                                {msg.sender === "user" ? "User" : "Bot"}:
                              </span>{" "}
                              {msg.text}
                            </div>
                            {msg.file_url && (
                              <button
                                onClick={() => handleDownload(msg.s3_key)}
                                className="mt-1 underline font-bold text-white text-sm"
                              >
                                Download file
                              </button>
                            )}
                            <div className="text-xs text-gray-500 mt-1 self-end">
                              {new Date(msg.timestamp).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          </main>
        )}
      </div>
      {(actionLoading || (view === "users" && loadingUsers) || (view === "chats" && loadingChats)) && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-white text-lg">Loading...</div>
        </div>
      )}
      {summaryModal.open && summaryModal.user && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {summaryModal.user.name}
                </h2>
                <p className="text-sm text-gray-600">{summaryModal.user.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setSummaryModal({ open: false, loading: false, text: "", user: null })}
                className="rounded px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            <h3 className="mb-2 text-base font-semibold text-gray-800">
              Chat Summary
            </h3>
            <div className="max-h-72 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              {summaryModal.loading ? (
                <p>Fetching summary...</p>
              ) : (
                <p className="whitespace-pre-wrap">{summaryModal.text}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
